import type {
  BarcodeTemplate,
  BarcodeTemplateInput,
  BarcodeWidthPolicy,
  Code128Template,
  CompiledTemplate,
  FontTemplate,
  QrCodeTemplate,
  TemplateDefaults,
} from "../types/template";
import { cloneWidthPolicy } from "../utils/barcode";

export const DEFAULT_FONT: FontTemplate = {
  family: "0",
  heightMm: 3,
  widthMm: 2,
  lineSpacingMm: 0.4,
  maxLines: 1,
  align: "left",
  overflowStrategy: "clip",
  minHeightMm: 1.2,
};

const DEFAULT_BARCODE_WIDTH_POLICY: BarcodeWidthPolicy = {
  mode: "fixed-module",
  moduleWidthMm: 0.5,
};

export const DEFAULT_CODE128: Code128Template = {
  symbology: "code128",
  heightMm: 12,
  widthPolicy: DEFAULT_BARCODE_WIDTH_POLICY,
  hAlign: "left",
  wideToNarrowRatio: 2,
  printInterpretationLine: false,
  interpretationAboveCode: false,
  checkDigit: false,
  optimizeNumeric: false,
};

export const DEFAULT_QRCODE: QrCodeTemplate = {
  symbology: "qrcode",
  magnification: 4,
  errorCorrection: "M",
  hAlign: "left",
};

function resolveBarcode(
  widthDefaults: BarcodeTemplateInput | undefined,
): BarcodeTemplate {
  if (widthDefaults?.symbology === "qrcode") {
    return {
      symbology: "qrcode",
      magnification:
        widthDefaults.magnification ?? DEFAULT_QRCODE.magnification,
      errorCorrection:
        widthDefaults.errorCorrection ?? DEFAULT_QRCODE.errorCorrection,
      hAlign: widthDefaults.hAlign ?? DEFAULT_QRCODE.hAlign,
    };
  }

  const widthPolicy = cloneWidthPolicy(
    widthDefaults?.widthPolicy ?? DEFAULT_CODE128.widthPolicy,
  );

  return {
    ...DEFAULT_CODE128,
    ...(widthDefaults ?? {}),
    symbology: "code128" as const,
    widthPolicy,
  };
}

export function resolveCompileDefaults(
  defaults: TemplateDefaults | undefined,
): CompiledTemplate["defaults"] {
  return {
    locale: defaults?.locale ?? "en-US",
    timeZone: defaults?.timeZone,
    encoding: defaults?.encoding ?? "cp1252",
    font: {
      ...DEFAULT_FONT,
      ...(defaults?.font ?? {}),
    },
    barcode: resolveBarcode(defaults?.barcode),
  };
}
